import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Copy, X, Check } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const templateNameSchema = z.object({
  name: z.string().min(2, "Template name must be at least 2 characters"),
  description: z.string().optional(),
});

type TemplateNameFormData = z.infer<typeof templateNameSchema>;

interface TemplateNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string, description?: string) => Promise<void>;
  title: string;
  subtitle: string;
  existingName?: string;
  existingDescription?: string;
  action: string;
}

export function TemplateNameModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  subtitle,
  existingName = "",
  existingDescription = "",
  action
}: TemplateNameModalProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<TemplateNameFormData>({
    resolver: zodResolver(templateNameSchema),
    defaultValues: {
      name: existingName ? `${existingName} (Copy)` : "",
      description: existingDescription || "",
    },
  });

  // Update form when existing values change
  React.useEffect(() => {
    if (isOpen) {
      form.reset({
        name: existingName ? `${existingName} (Copy)` : "",
        description: existingDescription || "",
      });
    }
  }, [existingName, existingDescription, isOpen, form]);

  const onSubmit = async (data: TemplateNameFormData) => {
    setIsSubmitting(true);
    try {
      await onConfirm(data.name, data.description);
      onClose();
      form.reset();
    } catch (error) {
      console.error("Error confirming action:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    form.reset();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center">
              <Copy className="h-4 w-4 text-blue-600" />
            </div>
            {title}
          </DialogTitle>
          {subtitle && (
            <div className="text-sm text-muted-foreground">
              {subtitle}
            </div>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter template name..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter template description..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-foreground"></div>
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {action}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}